import React, { useEffect, useMemo, useState } from "react";

import {
    createDriver,
    getDrivers,
    updateDriver,
    deleteDriver,
    assignShipmentToDriver,
    getDriverAssignments,
    getDriverDashboard,
    getDriverStatistics,
    getDriverPerformance,
    getDriverLocation,
} from "../../api/driverService";

import {
    PageHeader,
    Panel,
    KpiCard,
    InfoRow,
} from "../../components/ui/AdminUI";

import DataTable from "../../components/ui/DataTable";
import StatusBadge from "../../components/ui/StatusBadge";

import {
    TextInput,
    NumberInput,
    SelectBox,
    PrimaryButton,
    SuccessButton,
    SecondaryButton,
    DangerButton,
} from "../../components/ui/FormControls";

const EMPTY_DRIVER_FORM = {
    loginId: "",
    licenseNumber: "",
    vehicleNumber: "",
    status: "Available",
};

const EMPTY_ASSIGNMENT_FORM = {
    shipmentId: "",
    notes: "",
};

const DRIVER_STATUSES = [
    "Available",
    "Assigned",
    "Driving",
    "Loading",
    "Resting",
    "Offline",
];

export default function DriverPage() {
    const [drivers, setDrivers] = useState([]);
    const [assignments, setAssignments] = useState([]);

    const [selectedDriverId, setSelectedDriverId] = useState("");
    const [selectedDriver, setSelectedDriver] = useState(null);

    const [dashboard, setDashboard] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [performance, setPerformance] = useState(null);
    const [location, setLocation] = useState(null);

    const [driverForm, setDriverForm] = useState(EMPTY_DRIVER_FORM);
    const [assignmentForm, setAssignmentForm] = useState(
        EMPTY_ASSIGNMENT_FORM
    );

    const [editingDriverId, setEditingDriverId] = useState(null);

    const [loadingDrivers, setLoadingDrivers] = useState(true);
    const [loadingAssignments, setLoadingAssignments] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);

    const [savingDriver, setSavingDriver] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        loadPage();
    }, []);

    const loadPage = async () => {
        await Promise.all([
            loadDrivers(),
            loadOptionalDashboardData(),
        ]);
    };

    const loadDrivers = async () => {
        try {
            setLoadingDrivers(true);
            setError("");

            const data = await getDrivers();
            const list = Array.isArray(data) ? data : [];

            setDrivers(list);

            setSelectedDriverId((current) => {
                if (
                    current &&
                    list.some(
                        (driver) =>
                            Number(driver.id) === Number(current)
                    )
                ) {
                    return current;
                }

                return list[0]?.id
                    ? String(list[0].id)
                    : "";
            });
        } catch (err) {
            console.error("Driver loading failed:", err);

            setError(
                getErrorMessage(
                    err,
                    "Failed to load drivers."
                )
            );
        } finally {
            setLoadingDrivers(false);
        }
    };

    /*
     * These requests are additional.
     * If the corresponding backend endpoints are not ready yet,
     * The main CRUD page will continue to work.
     */
    const loadOptionalDashboardData = async () => {
        const [dashboardResult, statisticsResult] =
            await Promise.allSettled([
                getDriverDashboard(),
                getDriverStatistics(),
            ]);

        if (dashboardResult.status === "fulfilled") {
            setDashboard(dashboardResult.value);
        }

        if (statisticsResult.status === "fulfilled") {
            setStatistics(statisticsResult.value);
        }
    };

    const loadDriverWorkspace = async (driverId = selectedDriverId) => {
        const numericDriverId = Number(driverId);

        if (
            !Number.isInteger(numericDriverId) ||
            numericDriverId <= 0
        ) {
            setError("Select a valid driver.");
            return;
        }

        try {
            setLoadingDetails(true);
            setLoadingAssignments(true);
            setError("");
            setSuccess("");

            const driver =
                drivers.find(
                    (item) =>
                        Number(item.id) === numericDriverId
                ) || null;

            setSelectedDriver(driver);

            const [
                assignmentsResult,
                performanceResult,
                locationResult,
            ] = await Promise.allSettled([
                getDriverAssignments(numericDriverId),
                getDriverPerformance(numericDriverId),
                getDriverLocation(numericDriverId),
            ]);

            if (assignmentsResult.status === "fulfilled") {
                setAssignments(
                    Array.isArray(assignmentsResult.value)
                        ? assignmentsResult.value
                        : []
                );
            } else {
                setAssignments([]);
                setError(
                    getErrorMessage(
                        assignmentsResult.reason,
                        "Failed to load driver assignments."
                    )
                );
            }

            setPerformance(
                performanceResult.status === "fulfilled"
                    ? performanceResult.value
                    : null
            );

            setLocation(
                locationResult.status === "fulfilled"
                    ? locationResult.value
                    : null
            );
        } finally {
            setLoadingDetails(false);
            setLoadingAssignments(false);
        }
    };

    const handleSaveDriver = async (event) => {
        event.preventDefault();

        setError("");
        setSuccess("");

        const loginId = Number(driverForm.loginId);

        if (!Number.isInteger(loginId) || loginId <= 0) {
            setError("Valid Login ID is required.");
            return;
        }

        if (!driverForm.licenseNumber.trim()) {
            setError("Driver licence number is required.");
            return;
        }

        const payload = {
            loginId,
            licenseNumber:
                driverForm.licenseNumber.trim(),
            vehicleNumber:
                driverForm.vehicleNumber.trim() || null,
            status:
                driverForm.status || "Available",
        };

        try {
            setSavingDriver(true);

            if (editingDriverId) {
                await updateDriver(
                    Number(editingDriverId),
                    payload
                );

                setSuccess("Driver updated successfully.");
            } else {
                await createDriver(payload);
                setSuccess("Driver created successfully.");
            }

            resetDriverForm();
            await loadDrivers();
            await loadOptionalDashboardData();
        } catch (err) {
            console.error("Driver save failed:", err);

            setError(
                getErrorMessage(
                    err,
                    editingDriverId
                        ? "Failed to update driver."
                        : "Failed to create driver."
                )
            );
        } finally {
            setSavingDriver(false);
        }
    };

    const handleEditDriver = (driver) => {
        setEditingDriverId(driver.id);

        setDriverForm({
            loginId:
                driver.loginId ??
                driver.userId ??
                "",
            licenseNumber:
                driver.licenseNumber ??
                driver.driverLicenseNumber ??
                "",
            vehicleNumber:
                driver.vehicleNumber ??
                driver.vehicleRegistrationNumber ??
                "",
            status:
                driver.status ??
                (driver.isAvailable
                    ? "Available"
                    : "Assigned"),
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    const resetDriverForm = () => {
        setEditingDriverId(null);
        setDriverForm(EMPTY_DRIVER_FORM);
    };

    const handleDeleteDriver = async (driverId) => {
        if (!window.confirm("Delete this driver?")) {
            return;
        }

        try {
            setDeletingId(driverId);
            setError("");
            setSuccess("");

            await deleteDriver(driverId);

            setDrivers((current) =>
                current.filter(
                    (driver) =>
                        Number(driver.id) !==
                        Number(driverId)
                )
            );

            if (
                Number(selectedDriverId) ===
                Number(driverId)
            ) {
                setSelectedDriverId("");
                setSelectedDriver(null);
                setAssignments([]);
                setPerformance(null);
                setLocation(null);
            }

            setSuccess("Driver deleted successfully.");
            await loadOptionalDashboardData();
        } catch (err) {
            console.error("Driver deletion failed:", err);

            setError(
                getErrorMessage(
                    err,
                    "Failed to delete driver."
                )
            );
        } finally {
            setDeletingId(null);
        }
    };

    const handleAssignShipment = async (event) => {
        event.preventDefault();

        setError("");
        setSuccess("");

        const driverId = Number(selectedDriverId);
        const shipmentId = Number(
            assignmentForm.shipmentId
        );

        if (
            !Number.isInteger(driverId) ||
            driverId <= 0
        ) {
            setError("Select a driver.");
            return;
        }

        if (
            !Number.isInteger(shipmentId) ||
            shipmentId <= 0
        ) {
            setError("Valid Shipment ID is required.");
            return;
        }

        try {
            setAssigning(true);

            await assignShipmentToDriver(driverId, {
                shipmentId,
                notes:
                    assignmentForm.notes.trim() || null,
            });

            setAssignmentForm(EMPTY_ASSIGNMENT_FORM);
            setSuccess(
                "Shipment assigned to driver successfully."
            );

            await loadDriverWorkspace(driverId);
            await loadDrivers();
            await loadOptionalDashboardData();
        } catch (err) {
            console.error(
                "Driver assignment failed:",
                err
            );

            setError(
                getErrorMessage(
                    err,
                    "Failed to assign shipment."
                )
            );
        } finally {
            setAssigning(false);
        }
    };

    const pageStats = useMemo(() => {
        const totalDrivers =
            numberOrNull(
                statistics?.totalDrivers ??
                dashboard?.totalDrivers
            ) ?? drivers.length;

        const availableDrivers =
            numberOrNull(
                statistics?.availableDrivers ??
                dashboard?.availableDrivers
            ) ??
            drivers.filter((driver) =>
                isDriverStatus(driver, "available")
            ).length;

        const activeDrivers =
            numberOrNull(
                statistics?.activeDrivers ??
                dashboard?.activeDrivers
            ) ??
            drivers.filter((driver) => {
                const status = getDriverStatus(driver)
                    .toLowerCase();

                return [
                    "assigned",
                    "driving",
                    "loading",
                ].includes(status);
            }).length;

        const averageScore =
            numberOrNull(
                statistics?.averageDriverScore ??
                statistics?.averageSafetyScore ??
                dashboard?.averageDriverScore
            ) ??
            calculateAverageDriverScore(drivers);

        return {
            totalDrivers,
            availableDrivers,
            activeDrivers,
            averageScore,
        };
    }, [drivers, dashboard, statistics]);

    const driverColumns = [
        {
            key: "id",
            label: "ID",
        },
        {
            key: "driver",
            label: "Driver",
            render: (row) => (
                <div>
                    <p className="font-bold text-slate-900">
                        {getDriverName(row)}
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                        Login ID:{" "}
                        {row.loginId ??
                            row.userId ??
                            "�"}
                    </p>
                </div>
            ),
        },
        {
            key: "licenseNumber",
            label: "Licence",
            render: (row) =>
                row.licenseNumber ??
                row.driverLicenseNumber ??
                "�",
        },
        {
            key: "vehicleNumber",
            label: "Vehicle",
            render: (row) =>
                row.vehicleNumber ??
                row.vehicleRegistrationNumber ??
                row.vehicle?.registrationNumber ??
                "�",
        },
        {
            key: "status",
            label: "Status",
            render: (row) => (
                <StatusBadge
                    status={getDriverStatus(row)}
                />
            ),
        },
        {
            key: "score",
            label: "AI Score",
            render: (row) => {
                const score =
                    row.aiScore ??
                    row.driverScore ??
                    row.safetyScore ??
                    row.trustScore;

                return score === null ||
                    score === undefined
                    ? "�"
                    : `${Number(score).toFixed(1)}%`;
            },
        },
        {
            key: "actions",
            label: "Actions",
            render: (row) => (
                <div className="flex flex-wrap gap-2">
                    <PrimaryButton
                        className="px-3 py-2"
                        onClick={() => {
                            setSelectedDriverId(
                                String(row.id)
                            );
                            loadDriverWorkspace(row.id);
                        }}
                    >
                        Open
                    </PrimaryButton>

                    <SecondaryButton
                        className="px-3 py-2"
                        onClick={() =>
                            handleEditDriver(row)
                        }
                    >
                        Edit
                    </SecondaryButton>

                    <DangerButton
                        className="px-3 py-2"
                        disabled={deletingId === row.id}
                        onClick={() =>
                            handleDeleteDriver(row.id)
                        }
                    >
                        {deletingId === row.id
                            ? "Deleting..."
                            : "Delete"}
                    </DangerButton>
                </div>
            ),
        },
    ];

    const assignmentColumns = [
        {
            key: "id",
            label: "ID",
        },
        {
            key: "shipmentId",
            label: "Shipment",
            render: (row) =>
                row.shipmentId ??
                row.shipment?.id ??
                "�",
        },
        {
            key: "trackingNumber",
            label: "Tracking",
            render: (row) =>
                row.trackingNumber ??
                row.shipment?.trackingNumber ??
                "�",
        },
        {
            key: "status",
            label: "Status",
            render: (row) => (
                <StatusBadge
                    status={
                        row.status ??
                        row.assignmentStatus ??
                        "Assigned"
                    }
                />
            ),
        },
        {
            key: "assignedAt",
            label: "Assigned",
            render: (row) =>
                formatDateTime(
                    row.assignedAt ??
                    row.createdAt
                ),
        },
        {
            key: "completedAt",
            label: "Completed",
            render: (row) =>
                formatDateTime(row.completedAt),
        },
        {
            key: "notes",
            label: "Notes",
            render: (row) =>
                row.notes || "�",
        },
    ];

    return (
        <div className="space-y-6">
            <PageHeader
                title="AI Driver Management Center"
                subtitle="Manage drivers, shipment assignments, availability, performance and live operational data."
                action={
                    <SecondaryButton
                        onClick={loadPage}
                        disabled={loadingDrivers}
                    >
                        {loadingDrivers
                            ? "Refreshing..."
                            : "Refresh Drivers"}
                    </SecondaryButton>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            {success && (
                <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-green-700">
                    {success}
                </div>
            )}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                    title="Total Drivers"
                    value={pageStats.totalDrivers}
                    color="blue"
                />

                <KpiCard
                    title="Available"
                    value={pageStats.availableDrivers}
                    color="green"
                />

                <KpiCard
                    title="Active Assignments"
                    value={pageStats.activeDrivers}
                    color="purple"
                />

                <KpiCard
                    title="Average AI Score"
                    value={`${pageStats.averageScore.toFixed(
                        1
                    )}%`}
                    color="teal"
                />
            </div>

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <Panel
                    title={
                        editingDriverId
                            ? "Edit Driver"
                            : "Create Driver"
                    }
                    subtitle="Register a driver account and operational information."
                >
                    <form
                        onSubmit={handleSaveDriver}
                        className="space-y-4"
                    >
                        <NumberInput
                            label="Login ID"
                            value={driverForm.loginId}
                            onChange={(event) =>
                                setDriverForm((current) => ({
                                    ...current,
                                    loginId:
                                        event.target.value,
                                }))
                            }
                            placeholder="Login user ID"
                            min="1"
                            required
                        />

                        <TextInput
                            label="Driver Licence Number"
                            value={
                                driverForm.licenseNumber
                            }
                            onChange={(event) =>
                                setDriverForm((current) => ({
                                    ...current,
                                    licenseNumber:
                                        event.target.value,
                                }))
                            }
                            placeholder="Licence number"
                            required
                        />

                        <TextInput
                            label="Vehicle Number"
                            value={
                                driverForm.vehicleNumber
                            }
                            onChange={(event) =>
                                setDriverForm((current) => ({
                                    ...current,
                                    vehicleNumber:
                                        event.target.value,
                                }))
                            }
                            placeholder="Vehicle registration number"
                        />

                        <SelectBox
                            label="Driver Status"
                            value={driverForm.status}
                            onChange={(event) =>
                                setDriverForm((current) => ({
                                    ...current,
                                    status:
                                        event.target.value,
                                }))
                            }
                        >
                            {DRIVER_STATUSES.map((status) => (
                                <option
                                    key={status}
                                    value={status}
                                >
                                    {status}
                                </option>
                            ))}
                        </SelectBox>

                        <div className="flex flex-wrap gap-3">
                            <SuccessButton
                                type="submit"
                                disabled={savingDriver}
                            >
                                {savingDriver
                                    ? "Saving..."
                                    : editingDriverId
                                        ? "Update Driver"
                                        : "Create Driver"}
                            </SuccessButton>

                            {editingDriverId && (
                                <SecondaryButton
                                    onClick={
                                        resetDriverForm
                                    }
                                >
                                    Cancel Editing
                                </SecondaryButton>
                            )}
                        </div>
                    </form>
                </Panel>

                <Panel
                    title="Shipment Assignment"
                    subtitle="Assign a shipment to an available driver."
                >
                    <form
                        onSubmit={handleAssignShipment}
                        className="space-y-4"
                    >
                        <SelectBox
                            label="Driver"
                            value={selectedDriverId}
                            onChange={(event) => {
                                const value =
                                    event.target.value;

                                setSelectedDriverId(value);
                                setSelectedDriver(null);
                                setAssignments([]);
                                setPerformance(null);
                                setLocation(null);
                            }}
                            required
                        >
                            <option value="">
                                Select driver
                            </option>

                            {drivers.map((driver) => (
                                <option
                                    key={driver.id}
                                    value={driver.id}
                                >
                                    {getDriverName(driver)} �{" "}
                                    {getDriverStatus(driver)}
                                </option>
                            ))}
                        </SelectBox>

                        <NumberInput
                            label="Shipment ID"
                            value={
                                assignmentForm.shipmentId
                            }
                            onChange={(event) =>
                                setAssignmentForm(
                                    (current) => ({
                                        ...current,
                                        shipmentId:
                                            event.target.value,
                                    })
                                )
                            }
                            placeholder="Shipment ID"
                            min="1"
                            required
                        />

                        <TextInput
                            label="Assignment Notes"
                            value={assignmentForm.notes}
                            onChange={(event) =>
                                setAssignmentForm(
                                    (current) => ({
                                        ...current,
                                        notes:
                                            event.target.value,
                                    })
                                )
                            }
                            placeholder="Optional assignment notes"
                        />

                        <div className="flex flex-wrap gap-3">
                            <PrimaryButton
                                type="submit"
                                disabled={assigning}
                            >
                                {assigning
                                    ? "Assigning..."
                                    : "Assign Shipment"}
                            </PrimaryButton>

                            <SecondaryButton
                                onClick={() =>
                                    loadDriverWorkspace()
                                }
                                disabled={
                                    loadingDetails ||
                                    !selectedDriverId
                                }
                            >
                                {loadingDetails
                                    ? "Loading..."
                                    : "Load Driver Workspace"}
                            </SecondaryButton>
                        </div>
                    </form>
                </Panel>
            </div>

            <Panel
                title="Driver Registry"
                subtitle="Registered drivers and their current operational status."
            >
                {loadingDrivers ? (
                    <p className="text-slate-500">
                        Loading drivers...
                    </p>
                ) : (
                    <DataTable
                        columns={driverColumns}
                        rows={drivers}
                        rowKey="id"
                        emptyText="No drivers found."
                    />
                )}
            </Panel>

            {(selectedDriver ||
                performance ||
                location) && (
                    <Panel
                        title="Driver Intelligence"
                        subtitle="Performance, safety, workload and location information."
                    >
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <InfoRow
                                label="Driver"
                                value={
                                    selectedDriver
                                        ? getDriverName(
                                            selectedDriver
                                        )
                                        : "�"
                                }
                            />

                            <InfoRow
                                label="Status"
                                value={
                                    <StatusBadge
                                        status={
                                            selectedDriver
                                                ? getDriverStatus(
                                                    selectedDriver
                                                )
                                                : "Unknown"
                                        }
                                    />
                                }
                            />

                            <InfoRow
                                label="Safety Score"
                                value={formatScore(
                                    performance?.safetyScore ??
                                    selectedDriver?.safetyScore
                                )}
                            />

                            <InfoRow
                                label="Trust Score"
                                value={formatScore(
                                    performance?.trustScore ??
                                    selectedDriver?.trustScore
                                )}
                            />

                            <InfoRow
                                label="Completed Deliveries"
                                value={
                                    performance?.completedDeliveries ??
                                    selectedDriver?.completedDeliveries ??
                                    "�"
                                }
                            />

                            <InfoRow
                                label="Active Assignments"
                                value={
                                    performance?.activeAssignments ??
                                    assignments.filter(
                                        (item) =>
                                            String(
                                                item.status || ""
                                            ).toLowerCase() !==
                                            "completed"
                                    ).length
                                }
                            />

                            <InfoRow
                                label="Latitude"
                                value={
                                    location?.latitude ??
                                    selectedDriver?.latitude ??
                                    "�"
                                }
                            />

                            <InfoRow
                                label="Longitude"
                                value={
                                    location?.longitude ??
                                    selectedDriver?.longitude ??
                                    "�"
                                }
                            />

                            <InfoRow
                                label="Fatigue Level"
                                value={
                                    performance?.fatigueLevel ??
                                    selectedDriver?.fatigueLevel ??
                                    "Not evaluated"
                                }
                            />

                            <InfoRow
                                label="Working Hours"
                                value={
                                    performance?.workingHours ??
                                    selectedDriver?.workingHours ??
                                    "�"
                                }
                            />

                            <InfoRow
                                label="Next Rest"
                                value={formatDateTime(
                                    performance?.nextRestAt ??
                                    selectedDriver?.nextRestAt
                                )}
                            />

                            <InfoRow
                                label="Last Location Update"
                                value={formatDateTime(
                                    location?.updatedAt ??
                                    location?.lastUpdated
                                )}
                            />
                        </div>

                        {(location?.latitude ??
                            selectedDriver?.latitude) != null &&
                            (location?.longitude ??
                                selectedDriver?.longitude) !=
                            null && (
                                <div className="mt-5">
                                    <a
                                        href={`https://www.google.com/maps?q=${location?.latitude ??
                                            selectedDriver?.latitude
                                            },${location?.longitude ??
                                            selectedDriver?.longitude
                                            }`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-semibold text-blue-700 hover:underline"
                                    >
                                        Open driver location in map
                                    </a>
                                </div>
                            )}
                    </Panel>
                )}

            <Panel
                title="Driver Assignments"
                subtitle={
                    selectedDriverId
                        ? `Shipment assignments for driver #${selectedDriverId}.`
                        : "Select a driver to load assignments."
                }
            >
                {loadingAssignments ? (
                    <p className="text-slate-500">
                        Loading assignments...
                    </p>
                ) : (
                    <DataTable
                        columns={assignmentColumns}
                        rows={assignments}
                        rowKey="id"
                        emptyText={
                            selectedDriverId
                                ? "No assignments found for this driver."
                                : "Select a driver first."
                        }
                    />
                )}
            </Panel>
        </div>
    );
}

function getDriverName(driver) {
    if (!driver) return "Unknown driver";

    const fullName = [
        driver.name ??
        driver.login?.name ??
        driver.user?.name,
        driver.lastName ??
        driver.login?.lastName ??
        driver.user?.lastName,
    ]
        .filter(Boolean)
        .join(" ")
        .trim();

    return (
        fullName ||
        driver.fullName ||
        driver.userName ||
        driver.login?.userName ||
        driver.login?.email ||
        driver.email ||
        `Driver #${driver.id}`
    );
}

function getDriverStatus(driver) {
    if (!driver) return "Unknown";

    if (driver.status) {
        return String(driver.status);
    }

    if (driver.isAvailable === true) {
        return "Available";
    }

    if (driver.isActive === false) {
        return "Offline";
    }

    return "Assigned";
}

function isDriverStatus(driver, expectedStatus) {
    return (
        getDriverStatus(driver).toLowerCase() ===
        expectedStatus.toLowerCase()
    );
}

function calculateAverageDriverScore(drivers) {
    const scores = drivers
        .map(
            (driver) =>
                driver.aiScore ??
                driver.driverScore ??
                driver.safetyScore ??
                driver.trustScore
        )
        .map(Number)
        .filter(Number.isFinite);

    if (scores.length === 0) {
        return 0;
    }

    return (
        scores.reduce(
            (total, score) => total + score,
            0
        ) / scores.length
    );
}

function numberOrNull(value) {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return null;
    }

    const number = Number(value);

    return Number.isFinite(number)
        ? number
        : null;
}

function formatScore(value) {
    const score = Number(value);

    return Number.isFinite(score)
        ? `${score.toFixed(1)}%`
        : "�";
}

function formatDateTime(value) {
    if (!value) return "�";

    const date = new Date(value);

    return Number.isNaN(date.getTime())
        ? "�"
        : date.toLocaleString();
}

function getErrorMessage(error, fallback) {
    return (
        error?.response?.data?.message ||
        error?.response?.data?.title ||
        error?.message ||
        fallback
    );
}